<template>
  <div>
    <div v-if="!loadStatus && !Object.keys(publicRecipients).length">
      <Skeleton width="100%" height="3.5rem" v-for="i in 5" :key="i" />
    </div>

    <template v-if="loadStatus">
      <div class="recipient-header">
        <div class="search">
          <Search
            placeholder="Search by email or name"
            @input="value => (recipientsSearch = value)"
          />
        </div>
        <div>
          <div class="export-delete">
            <div v-if="projectDetails.state == 'wish_list'">
              <img
                @click="deleteRecipients"
                v-tooltip.bottom="'Remove'"
                class="delete-btn"
                :class="{ disabled: !this.selectedRowIds.length }"
                :src="
                  !!this.selectedRowIds.length
                    ? require('../../assets/delete-enabled.svg')
                    : require('../../assets/delete-disabled.svg')
                "
              />
            </div>
            <div>
              <button
                :disabled="exporting || !Object.keys(publicRecipients).length"
                :class="{ 'button-loading': exporting }"
                @click="exportRecipient"
                class="whitebutton-gemnote button-gemnote-glass--tiny gray"
              >
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <vue-good-table
        :columns="projectRecipientColumns"
        :rows="filteredRecipients"
        :class="{ 'is-editing': this.isEditingRow }"
        styleClass="vgt-table striped public-projects"
        :row-style-class="rowStyleClassFn"
        @on-cell-click="onCellClick"
        :search-options="{
          enabled: false
        }"
        :pagination-options="{
          enabled: false,
          mode: 'records',
          perPage: 15,
          perPageDropdown: [10, 15, 20],
          dropdownAllowAll: false,
          nextLabel: 'next',
          prevLabel: 'prev',
          rowsPerPageLabel: 'Rows per page',
          ofLabel: 'of',
          pageLabel: 'page',
          allLabel: 'All'
        }"
      >
        <template slot="table-row" slot-scope="publicRecipients">
          <span v-if="publicRecipients.column.field === 'action'">
            <label class="checkbox-container">
              <input
                v-model="selectedRowIds"
                name="recipient-ids"
                :value="publicRecipients.row.id"
                type="checkbox"
                :disabled="isEditingRow"
              />
              <span class="checkmark"></span>
            </label>
          </span>

          <span v-else-if="publicRecipients.column.field === 'name'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <input
                type="text"
                class="table-row-input"
                v-model="editedRow.name"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.name }}
            </span>
          </span>

          <span v-else-if="publicRecipients.column.field === 'email'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <input
                type="text"
                class="table-row-input"
                v-model="editedRow.email"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.email }}
            </span>
          </span>

          <span v-else-if="publicRecipients.column.field === 'phone'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <input
                type="text"
                class="table-row-input"
                v-model="editedRow.phone"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.phone }}
            </span>
          </span>

          <span v-else-if="publicRecipients.column.field === 'addressOne'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <input
                type="text"
                class="table-row-input"
                v-model="editedRow.addressOne"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.addressOne }}
            </span>
          </span>

          <span v-else-if="publicRecipients.column.field === 'addressTwo'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <input
                type="text"
                class="table-row-input"
                v-model="editedRow.addressTwo"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.addressTwo }}
            </span>
          </span>

          <span v-else-if="publicRecipients.column.field === 'city'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <input
                type="text"
                class="table-row-input"
                v-model="editedRow.city"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.city }}
            </span>
          </span>

          <span v-else-if="publicRecipients.column.field === 'state'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <region-select
                v-model="editedRow.state"
                :country="editedRow.country"
                :placeholder="'State'"
                :className="'select-input'"
                :region="editedRow.state"
                class="td-state-input"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.state }}
            </span>
          </span>

          <span v-else-if="publicRecipients.column.field === 'country'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <country-select
                class="td-country-input"
                v-model="editedRow.country"
                :country="editedRow.country"
                topCountry="US"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.country }}
            </span>
          </span>

          <template v-else-if="publicRecipients.column.field === 'zipCode'">
            <span
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <input
                type="text"
                class="table-row-input"
                v-model="editedRow.zipCode"
              />
            </span>
            <span v-else>
              {{ publicRecipients.row.zipCode }}
            </span>
          </template>

          <template v-else-if="publicRecipients.column.field === 'address'">
            <span class="enter-line">{{
              getFullAddress(publicRecipients.row)
            }}</span>
          </template>
          <template v-else-if="publicRecipients.column.field === 'apparelSize'">
            <ul>
              <li
                v-for="(apparel, index) in parseApparelSize(
                  publicRecipients.row.apparelSize
                )"
                :key="index"
              >
                <template
                  v-if="
                    currentEditRowIndex === publicRecipients.row.originalIndex
                  "
                >
                  <strong class="text-capitalize">{{ apparel.name }}</strong>

                  <div>
                    <ul class="">
                      <li
                        v-for="(option, index) in apparel.options"
                        :key="index"
                      >
                        <!--                        <span>{{ option.name }}: </span>-->
                        <select
                          class="apparel-size-option-input"
                          v-model="
                            editedRow.apparelSize[apparel.name][option.name]
                          "
                        >
                          <option
                            v-for="(option,
                            index) in getProductOptionApparelOption(
                              apparel.name,
                              option.name
                            )"
                            :key="index"
                            :value="option"
                          >
                            {{ option }}
                          </option>
                        </select>
                      </li>
                    </ul>
                  </div>
                </template>
                <p class="apparel-size-option" v-else>
                  <span class="text-capitalize">{{ apparel.name }}</span>

                  {{ apparel.optionsDisplay }}
                </p>
              </li>
            </ul>
          </template>
          <span v-else-if="publicRecipients.column.field === 'trackingUrl'">
            <template
              class="track-url-container"
              v-if="publicRecipients.row.trackingUrl"
            >
              <a
                style="white-space: nowrap; text-decoration: underline"
                :href="publicRecipients.row.trackingUrl"
                target="_blank"
              >
                {{ publicRecipients.row.trackingNumbers }}
              </a>
              &nbsp;
            </template>
            <span v-else> Not yet available </span>
          </span>

          <div v-else-if="publicRecipients.column.field === 'edit'">
            <div
              v-if="currentEditRowIndex === publicRecipients.row.originalIndex"
            >
              <div class="team-edit-actions">
                <button class="button-gemnote button-gemnote--tiny">
                  <span
                    @click.stop="
                      saveEdition(publicRecipients.row.originalIndex)
                    "
                    >Save</span
                  >
                </button>
              </div>
            </div>
            <div v-else class="tpeam-edit">
              <div
                class="table-edit-options"
                :class="{ 'edit-button-disabled': isEditingRow }"
              >
                <span>Edit</span>
              </div>
            </div>
          </div>
        </template>
        <div slot="emptystate">No Recipient data.</div>
      </vue-good-table>
      <div class="pb-12">
        <Pagination
          v-if="filteredRecipients.length"
          :total-items="totalCount"
          :page="serverParams.page"
          :items-per-page="serverParams.perPage"
          @page-change="pageChange"
          @perPageChange="handlePerPageChange"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import Pagination from '@/components/Universal/Pagination'
import Search from '@/components/Universal/Search.vue'
import { progressStatus } from '../../helpers'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'ProjectRecipientsList',
  props: {
    apparelSizeNeeded: {
      type: Boolean,
      required: true,
      default: false
    },

    projectDetails: {
      type: Object,
      required: true
    }
  },
  components: {
    VueGoodTable,
    Pagination,
    Search,
    Skeleton: () => import('@/components/Universal/Skeleton')
  },
  data() {
    return {
      serverParams: {
        page: 0,
        perPage: 50
      },
      currentEditRowIndex: null,
      currentUpdatedRowIndex: null,
      isEditingRow: false,
      editedRow: {},
      selectedRowIds: [],
      deletedRowIds: [],
      exporting: false,
      recipientsSearch: '',
      apparelSizes: ['M', 'L', 'S']
    }
  },
  computed: {
    loadStatus() {
      return progressStatus(this.loadingRecipients)
    },
    filteredRecipients() {
      if (this.recipientsSearch) {
        return this.publicRecipients.filter(recipient => {
          if (recipient.name && recipient.email) {
            return (
              recipient.name.indexOf(this.recipientsSearch) > -1 ||
              recipient.email.indexOf(this.recipientsSearch) > -1
            )
          }
        })
      } else {
        return this.publicRecipients
      }
    },
    projectRecipientColumns() {
      return [
        {
          label: '',
          field: 'action',
          sortable: false,
          width: '5%'
        },
        {
          label: 'Name',
          field: 'name',
          width: '25%'
        },
        {
          label: 'Company',
          field: 'company',
          width: '25%'
        },
        {
          label: 'Email',
          field: 'email',
          width: '15%'
        },
        {
          label: 'Phone',
          field: 'phone',
          width: '10%'
        },
        {
          label: 'Address Line 1',
          field: 'addressOne',
          width: '10%',
          hidden: !this.isEditingRow
        },
        {
          label: 'Address Line 2',
          field: 'addressTwo',
          width: '10%',
          hidden: !this.isEditingRow
        },
        {
          label: 'City',
          field: 'city',
          width: '15%',
          hidden: !this.isEditingRow
        },
        {
          label: 'State',
          field: 'state',
          width: '10%',
          hidden: !this.isEditingRow,
          tdClass: 'recipients-state-col'
        },
        {
          label: 'Country',
          field: 'country',
          width: '15%',
          hidden: !this.isEditingRow
          // tdClass: 'recipients-country-col'
        },
        {
          label: 'Zip',
          field: 'zipCode',
          width: '10%',
          hidden: !this.isEditingRow
        },
        {
          label: 'Address',
          field: 'address',
          width: '20%',
          hidden: this.isEditingRow
        },
        {
          label: 'Selection',
          field: 'apparelSize',
          width: '20%',
          hidden: !this.apparelSizeNeeded
        },
        {
          label: 'Tracking',
          field: 'trackingUrl',
          width: '20%',
          hidden: this.isEditingRow
        },
        {
          label: 'Edit',
          field: 'edit',
          sortable: false,
          width: '100px'
          // hidden: !(this.projectDetails.state === 'wish_list')
        }
      ]
    },
    ...mapState({
      loadingRecipients: state => state.project.searchingRecipient,
      totalCount: state => state.project.totalCount,
      publicRecipients: state => state.project.publicRecipients,
      deletingRecipient: state => state.project.deletingRecipient,
      updatingRecipientInfo: state => state.project.updatingPublicRecipientInfo
    })
  },
  created() {
    this.getPublicProjectRecipients({
      page: 1,
      perPage: this.serverParams.perPage,
      jwt: this.$route.query.token
    })
  },
  methods: {
    ...mapActions('project', ['getPublicProjectRecipients']),
    getFullAddress(row) {
      if (row.addressTwo) {
        return `${row.addressOne ? row.addressOne : ''}
        ${row.addressTwo ? row.addressTwo : ''}
        ${row.city ? row.city : ''} ${row.state ? row.state : ''} ${
          row.zipcode ? row.zipcode : ''
        }`
      } else {
        return `${row.addressOne ? row.addressOne : ''}
        ${row.city ? row.city : ''} ${row.state ? row.state : ''} ${
          row.zipcode ? row.zipcode : ''
        }`
      }
    },
    rowStyleClassFn(row) {
      return row.originalIndex === this.currentUpdatedRowIndex ||
        this.deletedRowIds.includes(row.id)
        ? 'updating-row'
        : 'not-loading'
    },
    onCellClick({ column, row }) {
      switch (column.field) {
        case 'edit':
          if (!this.isEditingRow) {
            this.currentEditRowIndex = row.originalIndex
            this.editedRow = row
            this.isEditingRow = true
          }
          break
      }
    },
    cancelEdition() {},
    async saveEdition() {
      const recipient = { ...this.editedRow }

      console.log({ recipient, pr: this.projectDetails })

      if (this.projectDetails.apparelSizeNeeded) {
        if (recipient.apparelSize) {
          recipient.apparelSize = Object.keys(recipient.apparelSize).map(
            key => {
              return {
                name: key,
                option: recipient.apparelSize[key]
              }
            }
          )
        } else {
          recipient.apparelSize = Object.keys(
            this.projectDetails.apparelSizeOptions
          ).map(key => {
            return {
              name: key,
              option: this.apparelSizes[key]
            }
          })
        }
      }

      this.currentUpdatedRowIndex = this.currentEditRowIndex

      this.currentEditRowIndex = null
      this.isEditingRow = false
      await this.$store.dispatch('project/updatePublicRecipient', recipient)
      this.currentUpdatedRowIndex = null
    },

    async deleteRecipients() {
      if (this.selectedRowIds.length) {
        this.deletedRowIds = this.selectedRowIds
        if (this.selectedRowIds.length === 1) {
          await this.$store.dispatch(
            'project/deletePublicRecipient',
            this.selectedRowIds
          )
        } else if (this.selectedRowIds.length > 1) {
          await this.$store.dispatch(
            'project/deletePublicRecipients',
            this.selectedRowIds
          )
        }
        this.deletedRowIds = []
        this.selectedRowIds = []
      }
    },

    pageChange(page) {
      this.updateParams({ page: page })
      this.getNextChuckOfRecipients()
      // scrollPageToTop()
    },
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this.serverParams, newProps)
    },
    handlePerPageChange(e) {
      this.serverParams.perPage = e
      this.getNextChuckOfRecipients()
    },
    getNextChuckOfRecipients() {
      this.getPublicProjectRecipients({
        page: this.serverParams.page + 1,
        perPage: this.serverParams.perPage,
        jwt: this.$route.query.token
      })
    },
    async exportRecipient() {
      this.exporting = true
      await this.$store.dispatch('project/exportPublicRecipient', data => {
        const downloadLink = window.document.createElement('a')
        downloadLink.href = data.order_recipient_csv_url
        downloadLink.click()
      })
      this.exporting = false
    },
    parseApparelSize(apparelSize) {
      let productsNames = Object.keys(apparelSize || {})
      return productsNames.map(productName => {
        const options = Object.keys(apparelSize[productName]).map(option => {
          return {
            name: option,
            option: apparelSize[productName][option]
          }
        })
        let productOptionsDisplay = options.map(option => {
          return `${option.name}: ${option.option}`
        })
        productOptionsDisplay = `(${productOptionsDisplay.join(', ')})`
        return {
          name: productName,
          optionsDisplay: productOptionsDisplay,
          options: options
        }
      })
    },
    getProductOptionApparelOption(productName, optionName) {
      const product = this.projectDetails.customApparelSizeOptions.find(
        product => product.name.toLowerCase() === productName.toLowerCase()
      )

      console.log({ product })
      if (product) {
        let options = product.options.find(option => {
          return option[optionName]
        })

        console.log({ options: options[optionName] })

        return options[optionName]
      }
      return []
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';
.track-url-container {
  display: flex;
  justify-content: flex-end;
  width: 170px;
}
.remove-dashes {
  text-decoration: none;
}
ul {
  li {
    font-size: unset !important;
    line-height: unset !important;
  }
}

.button-gemnote-glass--tiny {
  font-size: 0.813rem;
  font-weight: normal;
  /* height: 2.5rem; */
  width: auto;
  padding: 0 1rem;
}

.whitebutton-gemnote--small {
  font-size: 0.813rem;
  font-weight: normal;
  height: 2.5rem;
  width: auto;
  padding: 0 1rem;
  margin-right: 12px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.enter-line {
  white-space: pre-line;
}

.apparel-size {
  font-size: $content-font-size;
  display: block;
}

.recipient-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 0;
  margin-bottom: 1rem;

  .search {
    width: 30%;
  }
}

.search-input {
  padding-bottom: 20px !important;
  background-repeat: no-repeat;
  padding-left: 30px !important;
}

.export-delete {
  display: flex;
  justify-content: center;

  .delete-btn {
    cursor: pointer;
    padding-top: 3px;
    margin-right: 30px;

    &.disabled {
      cursor: not-allowed;
    }
  }
  button:disabled {
    cursor: not-allowed;
  }
}

.table-row-input {
  border: solid 1px #9b9b9b !important;
  font-size: 0.813rem;
  padding: 2px !important;
  height: unset;
  min-width: 100px;
}

.checkbox-container {
  input:disabled ~ .checkmark {
    border-color: rgba(37, 40, 42, 0.5);
  }
}

.edit-button-disabled {
  span {
    color: rgba(80, 80, 80, 0.5) !important;
  }
}

.td-country-input {
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  width: 180px !important;
}

.recipients-country-col {
  padding: 0 !important;
  vertical-align: middle;

  select {
    height: 4rem !important;
    background-size: 12px;
    background-position: calc(100% - 15px) 23px;
  }
}

.pb-12 {
  padding-bottom: 50px;
}

.apparel-size-option {
  font-size: 0.813rem;
}

.product-selection {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    font-size: 14px;
    display: flex;
    line-height: normal;
    height: 25px;
    align-items: center;

    span:first-child {
      text-transform: capitalize;
      line-height: unset;
      align-items: flex-start;
    }

    select {
      border: none;
      padding: 0;
      margin: 0;
      width: 84px;
      font-size: 14px;
      height: 100%;
      background-position: calc(100% - 50px) 3px;
      background-size: 10px;
    }

    select::-ms-expand {
      display: block;
    }
  }
}

.text-capitalize {
  text-transform: capitalize;
}

.apparel-size-option-input {
  height: unset !important;
  padding: 2px;
  background-position: calc(100% - 50px) 3px;
  border: none !important;
  min-width: 150px;
}
</style>
